import { z } from 'zod'
import { FreeTextCitedFactSchema, StructuredCitedFactSchema } from './citedFact'

// Scalar fields on an agreement/term a structured citation can point at.
export const AGREEMENT_FIELD_REFS = [
  'agreement.name',
  'agreement.description',
  'term.total_amount',
  'term.currency_code',
  'term.start_date',
  'term.end_date',
  'term.term_type',
  'term.auto_renew',
  'term.cancel_by',
  'term.primary_department',
  'term.cancellation_notice_days',
  'agreement.subsidiary',
  'agreement.payment_term',
  'agreement.payment_method',
] as const
export const AgreementFieldRefSchema = z.enum(AGREEMENT_FIELD_REFS)
export type AgreementFieldRef = z.infer<typeof AgreementFieldRefSchema>

// The agent input cites by `fieldRef` only; it carries no term/revision id (the resolved citation pins one, below).
const AgreementCitationInputBaseSchema = z.object({
  type: z.literal('agreement'),
  agreementId: z.string().describe('Identifier of the agreement record being cited'),
})

export const AgreementCitationAgentInputSchema = z.object({
  ...AgreementCitationInputBaseSchema.shape,
  fieldRef: AgreementFieldRefSchema.describe('Which scalar field on the agreement/term is being cited.'),
  anchor: z
    .string()
    .trim()
    .min(1)
    .nullable()
    .describe(
      'Verbatim phrase from the cited field, or null. Set this only for long free-text fields (e.g. a long-text agreement description) whose answer spans more than one sentence or roughly 20 words, where citing the whole field would be too broad; otherwise null.'
    ),
})

// A structured fact carries the `fieldRef` label key; a free-text quote carries no field label, so it
// stays the bare free-text variant. Modelled as a union (not a `fieldRef`-nullable bag) so a fact can
// never present both a field label and a free-text quote, nor neither.
export const StructuredCitedAgreementFactSchema = z.strictObject({
  ...StructuredCitedFactSchema.shape,
  fieldRef: AgreementFieldRefSchema,
})
export const CitedAgreementFactSchema = z.discriminatedUnion('kind', [
  StructuredCitedAgreementFactSchema,
  FreeTextCitedFactSchema,
])
export type CitedAgreementFact = z.infer<typeof CitedAgreementFactSchema>

// Entity grain: one citation per agreement, its cited fields stacked in `citedFacts`. The agent emits
// one input citation per field; the fold to this shape is a post-resolution merge.
export const AgreementCitationResolvedSchema = z.object({
  sourceType: z.literal('agreement'),
  sourceLabel: z.string(),
  agreementId: z.string(),
  supplierId: z.string(),
  // Point-in-time provenance pin, in the spirit of policy's `policyVersionId`: the main term's latest
  // revision at resolve time. Optional, since a term may carry no revisions.
  agreementTermRevisionId: z.string().optional(),
  citedFacts: z.array(CitedAgreementFactSchema).min(1),
})
export type AgreementCitationResolved = z.infer<typeof AgreementCitationResolvedSchema>
